import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { boxSizingStyles } from '../../styles/lit-shared';
import './ui-button';

@customElement('ui-double-confirm')
export class UiDoubleConfirm extends LitElement {
  static styles = [
    boxSizingStyles,
    css`
    :host {
      display: contents;
    }

    .backdrop {
      position: fixed;
      inset: 0;
      z-index: 200;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgb(6 6 15 / 75%);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      padding: var(--space-lg);
      animation: backdrop-in var(--duration-normal) var(--ease-out) both;
    }

    @keyframes backdrop-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .dialog {
      position: relative;
      width: 100%;
      max-width: 520px;
      max-height: 90dvh;
      overflow-y: auto;
      border-radius: var(--radius-lg);
      background: var(--palette-ink-900);
      border: 1px solid var(--color-border-strong);
      box-shadow: var(--shadow-lg), var(--shadow-glow-primary);
      animation: dialog-in var(--duration-normal) var(--ease-out) both;
    }

    @keyframes dialog-in {
      from {
        opacity: 0;
        transform: translateY(16px) scale(0.97);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .dialog-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--space-lg) var(--space-xl);
      border-bottom: 1px solid var(--color-border);
    }

    .dialog-title {
      font-size: 1.125rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .close-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border: none;
      border-radius: var(--radius-sm);
      background: transparent;
      color: var(--color-text-muted);
      cursor: pointer;
      font-size: 1.125rem;
      line-height: 1;
      transition:
        background var(--duration-fast) var(--ease-out),
        color var(--duration-fast) var(--ease-out);
    }

    .close-btn:hover {
      background: var(--color-surface-hover);
      color: var(--color-text);
    }

    .dialog-body {
      padding: var(--space-xl);
    }

    .message {
      margin: 0 0 var(--space-xl);
      color: var(--color-text-muted);
      font-size: 0.9375rem;
      line-height: 1.6;
    }

    .step-indicator {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      margin-bottom: var(--space-md);
      padding: 0.25rem 0.625rem;
      border-radius: var(--radius-full);
      background: rgb(255 255 255 / 4%);
      border: 1px solid var(--color-border);
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--color-text-muted);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      gap: var(--space-sm);
      flex-wrap: wrap;
    }

    @media (max-width: 639px) {
      .backdrop {
        align-items: flex-end;
        padding: 0;
      }

      .dialog {
        max-width: 100%;
        max-height: 85dvh;
        border-radius: var(--radius-lg) var(--radius-lg) 0 0;
      }
    }
  `,
  ];

  @property({ type: Boolean, reflect: true }) open = false;
  @property({ type: String }) title = 'Confirmar acción';
  @property({ type: String, attribute: 'step1-message' }) step1Message = '';
  @property({ type: String, attribute: 'step2-message' }) step2Message = '';
  @property({ type: String, attribute: 'confirm-label' }) confirmLabel = 'Confirmar';
  @property({ type: Boolean }) loading = false;
  @property({ type: Boolean }) destructive = true;

  @state() private step: 1 | 2 = 1;

  updated(changed: Map<string, unknown>) {
    if (changed.has('open') && !this.open) {
      this.step = 1;
    }
  }

  private close() {
    this.step = 1;
    this.dispatchEvent(
      new CustomEvent('modal-close', { bubbles: true, composed: true }),
    );
  }

  private handleBackdropClick(e: Event) {
    if (e.target === e.currentTarget && !this.loading) this.close();
  }

  private handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && !this.loading) this.close();
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this._boundKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._boundKeydown);
  }

  private _boundKeydown = (e: KeyboardEvent) => this.handleKeydown(e);

  private goToStep2() {
    this.step = 2;
  }

  private goBack() {
    this.step = 1;
  }

  private confirm() {
    if (this.loading) return;
    this.dispatchEvent(
      new CustomEvent('confirm', { bubbles: true, composed: true }),
    );
  }

  render() {
    if (!this.open) return html``;

    const message = this.step === 1 ? this.step1Message : this.step2Message;

    return html`
      <div class="backdrop" @click=${this.handleBackdropClick}>
        <div class="dialog" role="dialog" aria-modal="true" aria-label=${this.title}>
          <div class="dialog-header">
            <span class="dialog-title">${this.title}</span>
            <button
              class="close-btn"
              aria-label="Cerrar"
              ?disabled=${this.loading}
              @click=${this.close}
            >✕</button>
          </div>
          <div class="dialog-body">
            <span class="step-indicator">Paso ${this.step} de 2</span>
            <p class="message">${message}</p>
            <div class="actions">
              ${this.step === 1
                ? html`
                  <ui-button
                    variant="secondary"
                    ?disabled=${this.loading}
                    @ui-click=${this.close}
                  >Cancelar</ui-button>
                  <ui-button
                    ?disabled=${this.loading}
                    @ui-click=${this.goToStep2}
                  >Continuar</ui-button>
                `
                : html`
                  <ui-button
                    variant="secondary"
                    ?disabled=${this.loading}
                    @ui-click=${this.goBack}
                  >Volver</ui-button>
                  <ui-button
                    variant=${this.destructive ? 'danger' : 'primary'}
                    ?loading=${this.loading}
                    ?disabled=${this.loading}
                    @ui-click=${this.confirm}
                  >${this.confirmLabel}</ui-button>
                `}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ui-double-confirm': UiDoubleConfirm;
  }
}
